import { Brain, CheckCircle2, ChevronRight, Cpu, LifeBuoy, Search, Siren, UserRoundCog } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import type { Agent, AgentFlowNode, DependencyStatus, ModuleHealth } from '@/schemas/commons';

import { ModuleDetailLoading } from './ModuleDetail.loading';
import { useModuleByName } from './ModuleDetail.requests';
import { styles } from './ModuleDetail.styles';

const HEALTH_LABEL_KEY: Record<DependencyStatus, string> = {
	up: 'HEALTH_UP',
	degraded: 'HEALTH_DEGRADED',
	down: 'HEALTH_DOWN',
	disabled: 'HEALTH_DISABLED',
};

const HEALTH_DOT: Record<DependencyStatus, string> = {
	up: 'bg-green-500',
	degraded: 'bg-amber-500',
	down: 'bg-red-500',
	disabled: 'bg-grey-400',
};

const HEALTH_TEXT: Record<DependencyStatus, string> = {
	up: 'text-green-700',
	degraded: 'text-amber-700',
	down: 'text-red-700',
	disabled: 'text-subtle',
};

const TOOL_ICONS: Record<string, typeof Search> = {
	search_kb: Search,
	search_domain_kb: Search,
	handoff_to_team: UserRoundCog,
	get_platform_incidents: Siren,
	close_conversation: CheckCircle2,
};

const formatCurrency = (value: number): string => `$${value.toFixed(2)}`;
const formatCurrencySmall = (value: number): string => `$${value.toFixed(3)}`;

export const ModuleDetail = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'moduleDetail' });
	const { moduleName } = useParams();
	const { module: mod, isLoading, isError, notFound } = useModuleByName(moduleName);

	if (isLoading) return <ModuleDetailLoading />;
	if (notFound) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('NOT_FOUND', { name: moduleName })}</p>
			</main>
		);
	}
	if (isError || !mod) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('ERROR_LOAD')}</p>
			</main>
		);
	}

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<Link to="/modules" className="hover:text-default">
						{t('BREADCRUMB_PARENT')}
					</Link>
					<ChevronRight className="h-sm w-sm" />
					<span className="font-medium text-default">{mod.name}</span>
				</nav>
				<Link
					to="/conversations"
					className="inline-flex items-center gap-3xs text-[11px] text-subtle transition-colors hover:text-default"
				>
					{t('SEE_CONVERSATIONS')}
					<ChevronRight className="h-sm w-sm" />
				</Link>
			</header>

			<main className={styles.content}>
				<header className="flex flex-wrap items-start justify-between gap-md">
					<div className="flex items-center gap-xs">
						<span className={styles.moduleMark}>
							<LifeBuoy className="h-lg w-lg" />
						</span>
						<div>
							<p className="mb-2xs text-[11px] font-medium uppercase tracking-wider text-purple-700">{t('EYEBROW')}</p>
							<h1 className={`${styles.pageH1} flex items-center gap-xs`}>
								{mod.name}
								{mod.health ? (
									<span
										className={`inline-flex items-center gap-3xs text-[12px] font-normal ${
											HEALTH_TEXT[mod.health.status]
										}`}
									>
										<span className={`inline-block h-2xs w-2xs rounded-full ${HEALTH_DOT[mod.health.status]}`} />
										{t(HEALTH_LABEL_KEY[mod.health.status])}
									</span>
								) : (
									<span className="inline-flex items-center gap-3xs text-[12px] font-normal text-subtle">
										<span className="inline-block h-2xs w-2xs rounded-full bg-grey-400" />
										{t('STATUS_LIVE')}
									</span>
								)}
							</h1>
							<p className="mt-3xs font-mono text-[13px] text-subtle">{mod.path}</p>
						</div>
					</div>

					<dl className={styles.statsCard}>
						<div className="flex flex-col items-end gap-3xs">
							<dt className={styles.statLabel}>{t('STAT_TURNS')}</dt>
							<dd className={styles.statValue}>{mod.turns.last24h}</dd>
						</div>
						<div className="h-2xl w-[1px] self-end bg-subtle" />
						<div className="flex flex-col items-end gap-3xs">
							<dt className={styles.statLabel}>{t('STAT_ERRORS')}</dt>
							<dd className={styles.statValue}>{mod.turns.errors24h}</dd>
						</div>
						<div className="h-2xl w-[1px] self-end bg-subtle" />
						<div className="flex flex-col items-end gap-3xs">
							<dt className={styles.statLabelBrand}>{t('STAT_COST')}</dt>
							<dd className={styles.statValueBrand}>{formatCurrency(mod.costs.last24h)}</dd>
						</div>
					</dl>
				</header>

				<section className="border-b border-subtle pb-lg">
					<h2 className={styles.sectionH}>{t('DESCRIPTION_TITLE')}</h2>
					<p className={styles.sectionSub}>{t('DESCRIPTION_SUB')}</p>
					<p className="mt-md max-w-[68ch] text-[14px] leading-relaxed text-default">{mod.description}</p>
				</section>

				{mod.health && (
					<section className="border-b border-subtle pb-lg">
						<h2 className={styles.sectionH}>{t('HEALTH_TITLE')}</h2>
						<p className={styles.sectionSub}>{t('HEALTH_SUB')}</p>
						<HealthTable health={mod.health} />
					</section>
				)}

				<section className="flex flex-col gap-md">
					<header className="flex items-baseline justify-between">
						<div>
							<h2 className={styles.sectionH}>{t('COST_TITLE')}</h2>
							<p className={styles.sectionSub}>{t('COST_SUB')}</p>
						</div>
					</header>

					<dl className="grid grid-cols-2 gap-x-xl gap-y-md border-y border-subtle py-md md:grid-cols-4">
						<div className="flex flex-col gap-3xs">
							<dt className={styles.statLabel}>{t('COST_24H')}</dt>
							<dd className={styles.statValue}>{formatCurrency(mod.costs.last24h)}</dd>
						</div>
						<div className="flex flex-col gap-3xs">
							<dt className={styles.statLabel}>{t('COST_7D')}</dt>
							<dd className={styles.statValue}>{formatCurrency(mod.costs.last7d)}</dd>
						</div>
						<div className="flex flex-col gap-3xs">
							<dt className={styles.statLabel}>{t('COST_MONTH')}</dt>
							<dd className={styles.statValueBrand}>{formatCurrency(mod.costs.currentMonth)}</dd>
							<span className="text-[11px] tabular-nums text-subtle">
								{t('COST_PROJECTION', { value: formatCurrency(mod.costs.monthProjection) })}
							</span>
						</div>
						<div className="flex flex-col gap-3xs">
							<dt className={styles.statLabel}>{t('COST_PER_TURN')}</dt>
							<dd className={styles.statValue}>{formatCurrencySmall(mod.costs.avgPerTurn)}</dd>
							<span className="text-[11px] tabular-nums text-subtle">~5.2k in · 480 out</span>
						</div>
					</dl>

					<div>
						<p className="mb-xs text-[10px] font-medium uppercase tracking-wider text-subtle">{t('MODEL_BREAKDOWN')}</p>
						<ul className="flex flex-col">
							<li className="flex items-center justify-between border-b border-subtle/50 py-2xs text-[13px]">
								<div className="flex min-w-0 items-center gap-xs">
									<code className="font-mono font-medium text-default">claude-sonnet-4-6</code>
									<span className="text-[11px] text-subtle">
										{t('MODEL_CHAT')} · {mod.turns.last24h} calls
									</span>
								</div>
								<span className="font-semibold tabular-nums text-default">$4.89</span>
							</li>
							<li className="flex items-center justify-between py-2xs text-[13px]">
								<div className="flex min-w-0 items-center gap-xs">
									<code className="font-mono font-medium text-default">claude-haiku-4-5</code>
									<span className="text-[11px] text-subtle">{t('MODEL_RAG')} · 52 calls</span>
								</div>
								<span className="font-semibold tabular-nums text-default">$0.58</span>
							</li>
						</ul>
					</div>
				</section>

				{mod.agents.length > 0 && (
					<section className="flex flex-col gap-md">
						<header>
							<h2 className={styles.sectionH}>{t('AGENT_TITLE', { count: mod.agents.length })}</h2>
							<p className={styles.sectionSub}>{t('AGENT_SUB', { count: mod.agents.length })}</p>
						</header>

						{mod.agents.map((agent) => (
							<AgentCard key={agent.name} agent={agent} />
						))}
					</section>
				)}
			</main>
		</>
	);
};

type AgentCardProps = { agent: Agent };

const AgentCard = ({ agent }: AgentCardProps) => (
	<article className="flex flex-col gap-lg rounded-lg border border-subtle bg-white p-lg">
		<header className="flex flex-wrap items-start justify-between gap-md">
			<div className="flex items-start gap-xs">
				<span className={styles.agentAvatar}>{agent.name.charAt(0).toUpperCase()}</span>
				<div className="flex flex-col gap-3xs">
					<h3 className="text-[16px] font-semibold leading-tight text-default">{agent.name}</h3>
					<p className="font-mono text-[11px] leading-none text-subtle">{agent.path}</p>
				</div>
			</div>
			<div className="flex items-center gap-3xs">
				<span className={styles.badgeBrand}>
					<Brain className="h-sm w-sm" />
					{agent.pattern}
				</span>
				<span className={`${styles.badgeNeutral} font-mono`}>
					<Cpu className="h-sm w-sm" />
					{agent.model}
				</span>
			</div>
		</header>

		<p className="max-w-[68ch] text-[13px] leading-relaxed text-subtle">{agent.description}</p>

		<FlowDiagram flow={agent.flow} />

		<ToolsList tools={agent.tools} />
	</article>
);

type FlowDiagramProps = { flow: AgentFlowNode[] };

const FlowDiagram = ({ flow }: FlowDiagramProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'moduleDetail' });

	return (
		<div>
			<p className="mb-xs text-[10px] font-medium uppercase tracking-wider text-subtle">{t('FLOW_TITLE')}</p>
			<div className="relative overflow-x-auto pb-2xs">
				<div className="flex w-fit items-stretch gap-3xs text-[12px]">
					{flow.map((node, idx) => {
						const isLoopNode = node.kind === 'llm' || node.kind === 'tools';
						const cls = node.kind === 'end' ? styles.flowNodeEnd : isLoopNode ? styles.flowNodeActive : styles.flowNode;
						const labelKey =
							node.kind === 'pre'
								? 'FLOW_KIND_PRE'
								: node.kind === 'post'
									? 'FLOW_KIND_POST'
									: node.kind === 'llm'
										? 'FLOW_KIND_LLM'
										: node.kind === 'tools'
											? 'FLOW_KIND_TOOLS'
											: 'FLOW_KIND_END';

						return (
							<div key={`${node.name}-${node.kind}`} className="flex items-center gap-3xs">
								<div className={cls}>
									<span
										className={`text-[9px] uppercase tracking-wider ${
											isLoopNode ? 'text-purple-700' : node.kind === 'end' ? 'text-subtle' : 'text-subtle'
										}`}
									>
										{t(labelKey)}
									</span>
									<code className={`font-mono font-semibold ${isLoopNode ? 'text-purple-700' : 'text-default'}`}>
										{node.name}
									</code>
								</div>
								{idx < flow.length - 1 && <ChevronRight className={styles.flowArrow} />}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};

type HealthTableProps = { health: ModuleHealth };

const HealthTable = ({ health }: HealthTableProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'moduleDetail' });

	return (
		<div className="mt-md overflow-hidden rounded-lg border border-subtle">
			<table className="w-full text-left text-[12px]">
				<thead className="border-b border-subtle bg-grey-25/50">
					<tr>
						<th className="px-sm py-2xs text-[10px] font-medium uppercase tracking-wider text-subtle">
							{t('HEALTH_COL_DEP')}
						</th>
						<th className="px-sm py-2xs text-[10px] font-medium uppercase tracking-wider text-subtle">
							{t('HEALTH_COL_KIND')}
						</th>
						<th className="px-sm py-2xs text-[10px] font-medium uppercase tracking-wider text-subtle">
							{t('HEALTH_COL_REQUIRED')}
						</th>
						<th className="px-sm py-2xs text-[10px] font-medium uppercase tracking-wider text-subtle">
							{t('HEALTH_COL_STATUS')}
						</th>
						<th className="px-sm py-2xs text-right text-[10px] font-medium uppercase tracking-wider text-subtle">
							{t('HEALTH_COL_LATENCY')}
						</th>
					</tr>
				</thead>
				<tbody>
					{health.dependencies.map((dep) => (
						<tr key={`${dep.kind}-${dep.name}`} className="border-b border-subtle/50">
							<td className="px-sm py-2xs">
								<div className="font-medium text-default">{dep.name}</div>
								{dep.message && <div className="text-[11px] text-subtle">{dep.message}</div>}
							</td>
							<td className="px-sm py-2xs font-mono text-[11px] text-subtle">{dep.kind}</td>
							<td className="px-sm py-2xs text-[11px] text-subtle">
								{dep.required ? t('HEALTH_DEP_REQUIRED') : t('HEALTH_DEP_OPTIONAL')}
							</td>
							<td className="px-sm py-2xs">
								<span className={`inline-flex items-center gap-3xs text-[12px] ${HEALTH_TEXT[dep.status]}`}>
									<span className={`inline-block h-2xs w-2xs rounded-full ${HEALTH_DOT[dep.status]}`} />
									{t(HEALTH_LABEL_KEY[dep.status])}
								</span>
							</td>
							<td className="px-sm py-2xs text-right tabular-nums text-subtle">
								{dep.latencyMs != null ? `${dep.latencyMs} ms` : '—'}
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
};

type ToolsListProps = { tools: { name: string; description: string; callCount24h: number }[] };

const ToolsList = ({ tools }: ToolsListProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'moduleDetail' });
	const max = Math.max(...tools.map((tool) => tool.callCount24h), 1);

	return (
		<div>
			<div className="mb-xs flex items-baseline justify-between">
				<p className="text-[10px] font-medium uppercase tracking-wider text-subtle">
					{t('TOOLS_TITLE', { count: tools.length })}
				</p>
				<p className="text-[11px] text-subtle">{t('TOOLS_USAGE')}</p>
			</div>
			<ul className="grid grid-cols-1 gap-3xs lg:grid-cols-2">
				{tools.map((tool) => {
					const Icon = TOOL_ICONS[tool.name] ?? Search;
					const percent = (tool.callCount24h / max) * 100;

					return (
						<li
							key={tool.name}
							className="flex items-start gap-xs rounded-md px-xs py-2xs transition-colors hover:bg-grey-25"
						>
							<Icon className="mt-3xs h-md w-md shrink-0 text-subtle" />
							<div className="min-w-0 flex-1">
								<div className="flex items-center justify-between gap-xs">
									<code className="font-mono text-[13px] font-semibold leading-none text-default">{tool.name}</code>
									<span className="text-[11px] tabular-nums text-subtle">{tool.callCount24h}</span>
								</div>
								<p className="mt-3xs text-[12px] leading-tight text-subtle">{tool.description}</p>
								<div className="mt-2xs h-[4px] overflow-hidden rounded-full bg-grey-25">
									<div className="h-full bg-purple-500" style={{ width: `${percent}%` }} />
								</div>
							</div>
						</li>
					);
				})}
			</ul>
		</div>
	);
};
